import type {
  AppliedDiscount,
  CartEvaluationResult,
  EvaluatedCartLine,
  PricedCartLine,
  Promotion,
  PromotionConditions,
  PromotionKind,
  PromotionTarget,
  PromotionWithTargets,
} from './promotionTypes';
import type { CartEvaluationOptions } from './cartEvaluationOptions';
import { normalizePromoCode, usageCountForPromotion } from './cartEvaluationOptions';
import { resolvePromotionMoneyScale, roundArsHalfUp, runWithMoneyScale } from './promotionRounding';

type MutableLineState = {
  lineId: string;
  productId: string;
  categoryId?: string | null;
  brandId?: string | null;
  unitPriceOriginal: number;
  quantity: number;
  lineDiscountTotal: number;
  appliedDiscounts: AppliedDiscount[];
  hasNonStackablePromo: boolean;
};

type UnitSlot = {
  lineId: string;
  unitPrice: number;
};

const AUTO_KINDS: PromotionKind[] = [
  'percent_off',
  'fixed_off',
  'combo_buy_x_get_y',
  'combo_n_for_price',
];

export function isLineEligibleForAutoPromo(line: PricedCartLine): boolean {
  if (line.itemType === 'custom' || line.itemType === 'package') return false;
  if (line.isMisc) return false;
  if (!Number.isFinite(line.quantity) || line.quantity <= 0) return false;
  if (!Number.isFinite(line.unitPrice) || line.unitPrice < 0) return false;
  return true;
}

export function isPromotionScheduleActive(promotion: Promotion, now: Date): boolean {
  if (promotion.startsAt) {
    const start = Date.parse(promotion.startsAt);
    if (!Number.isNaN(start) && start > now.getTime()) return false;
  }
  if (promotion.endsAt) {
    const end = Date.parse(promotion.endsAt);
    if (!Number.isNaN(end) && end < now.getTime()) return false;
  }
  return true;
}

export function promotionAppliesToBranch(promotion: Promotion, branchId: string | null | undefined): boolean {
  const ids = promotion.branchIds;
  if (!ids || ids.length === 0) return true;
  if (!branchId) return true;
  return ids.includes(branchId);
}

/** Ausente/vacío = ambos canales (default DB + SQLite legacy sin columna). */
export function promotionAppliesToChannel(
  promotion: Promotion,
  channel: 'pos' | 'online'
): boolean {
  const channels = promotion.channels;
  if (!channels || channels.length === 0) return true;
  return channels.includes(channel);
}

export function buildTargetsByPromotionId(
  promotions: PromotionWithTargets[]
): Map<string, PromotionTarget[]> {
  const map = new Map<string, PromotionTarget[]>();
  for (const promo of promotions) {
    map.set(promo.id, promo.targets ?? []);
  }
  return map;
}

export function promotionMatchesLine(
  promotion: Promotion,
  targets: PromotionTarget[],
  line: PricedCartLine
): boolean {
  if (targets.length === 0) return true;
  return targets.some((target) => {
    if (target.targetType === 'product') return target.targetId === line.productId;
    // Target `variant`: matchea solo la variante comprada. Un target `product`
    // sigue matcheando todas las variantes del producto (backward-compat).
    if (target.targetType === 'variant') return Boolean(line.variantId) && target.targetId === line.variantId;
    if (target.targetType === 'category') return Boolean(line.categoryId) && target.targetId === line.categoryId;
    if (target.targetType === 'brand') return Boolean(line.brandId) && target.targetId === line.brandId;
    return false;
  });
}

/** IDs de producto en targets (sin duplicados). */
export function distinctProductTargetIds(targets: PromotionTarget[]): string[] {
  const ids = new Set<string>();
  for (const target of targets) {
    if (target.targetType === 'product') ids.add(target.targetId);
  }
  return [...ids];
}

/**
 * Monto fijo en carrito con 2+ productos target = combo (Opera + jugo).
 * Requiere al menos 1 unidad de cada producto listado; no basta con uno solo.
 */
export function promotionRequiresAllProductTargetsInCart(
  promotion: Promotion,
  targets: PromotionTarget[]
): boolean {
  if (promotion.kind !== 'fixed_off' || promotion.scope !== 'cart') return false;
  return distinctProductTargetIds(targets).length >= 2;
}

export function cartHasAllProductTargets(cart: PricedCartLine[], productTargetIds: string[]): boolean {
  if (productTargetIds.length === 0) return true;
  for (const productId of productTargetIds) {
    const hasUnit = cart.some((line) => line.productId === productId && line.quantity > 0);
    if (!hasUnit) return false;
  }
  return true;
}

function lineRemainingSubtotal(line: MutableLineState): number {
  const gross = line.unitPriceOriginal * line.quantity;
  return Math.max(0, gross - line.lineDiscountTotal);
}

function lineRemainingSubtotalForQuantity(line: MutableLineState, quantity: number): number {
  if (line.quantity <= 0 || quantity <= 0) return 0;
  return lineRemainingSubtotal(line) * Math.min(1, quantity / line.quantity);
}

function selectComboParticipantQuantities(
  matching: MutableLineState[],
  targets: PromotionTarget[]
): Map<string, number> | null {
  const quantities = new Map<string, number>();

  for (const productId of distinctProductTargetIds(targets).sort()) {
    const line = matching.find(
      (candidate) =>
        candidate.productId === productId &&
        candidate.quantity > 0 &&
        lineRemainingSubtotal(candidate) > 0
    );
    if (!line) return null;
    quantities.set(line.lineId, Math.min(1, line.quantity));
  }

  return quantities;
}

function cartScopeDiscountBase(
  lines: MutableLineState[],
  quantityAffectedByLine?: Map<string, number>
): number {
  return lines.reduce((sum, line) => {
    const quantity = quantityAffectedByLine?.get(line.lineId);
    const subtotal =
      quantity == null
        ? lineRemainingSubtotal(line)
        : lineRemainingSubtotalForQuantity(line, quantity);
    return sum + subtotal;
  }, 0);
}

function recordLineDiscount(
  line: MutableLineState,
  promotion: Promotion,
  amount: number,
  quantityAffected?: number
): void {
  const discountAmount = roundArsHalfUp(amount);
  if (discountAmount <= 0) return;
  line.lineDiscountTotal = roundArsHalfUp(line.lineDiscountTotal + discountAmount);
  line.appliedDiscounts.push({
    promotionId: promotion.id,
    promotionName: promotion.name,
    kind: promotion.kind,
    scope: promotion.scope,
    lineId: line.lineId,
    discountAmount,
    quantityAffected,
  });
  if (!promotion.stackable) {
    line.hasNonStackablePromo = true;
  }
}

function eligibleLinesForPromotion(
  lines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>,
  promotion: Promotion,
  targets: PromotionTarget[]
): MutableLineState[] {
  return lines.filter((line) => {
    const cartLine = cartLinesById.get(line.lineId);
    if (!cartLine || !isLineEligibleForAutoPromo(cartLine)) return false;
    if (!promotionMatchesLine(promotion, targets, cartLine)) return false;
    if (!promotion.stackable && line.hasNonStackablePromo) return false;
    return true;
  });
}

/**
 * Combos (buy-X-get-Y, N-for-price) operate on discrete "unit slots". A
 * fractional quantity (e.g. 2.5 kg sold loose) has no well-defined slot
 * count, so we only slot the whole-unit portion (floor) and leave any
 * fractional remainder out of the combo — this never grants a free/discounted
 * unit that wasn't actually purchased. A naive `i < line.quantity` loop would
 * instead run one extra iteration for any fractional quantity (e.g. 2.5 → 3
 * slots), over-crediting the discount.
 */
function expandUnitSlots(lines: MutableLineState[], cartLinesById: Map<string, PricedCartLine>): UnitSlot[] {
  const slots: UnitSlot[] = [];
  for (const line of lines) {
    const cartLine = cartLinesById.get(line.lineId);
    if (!cartLine) continue;
    const remaining = lineRemainingSubtotal(line);
    if (remaining <= 0) continue;
    const wholeUnits = Math.floor(line.quantity);
    if (wholeUnits <= 0) continue;
    const effectiveUnit = remaining / line.quantity;
    for (let i = 0; i < wholeUnits; i += 1) {
      slots.push({ lineId: line.lineId, unitPrice: effectiveUnit });
    }
  }
  return slots;
}

function distributeDiscountToLines(
  linesById: Map<string, MutableLineState>,
  promotion: Promotion,
  perLineAmounts: Map<string, number>,
  quantityAffectedByLine?: Map<string, number>
): void {
  for (const [lineId, amount] of perLineAmounts) {
    const line = linesById.get(lineId);
    if (!line || amount <= 0) continue;
    recordLineDiscount(line, promotion, amount, quantityAffectedByLine?.get(lineId));
  }
}

function allocateCartScopeDiscount(
  lines: MutableLineState[],
  matching: MutableLineState[],
  promotion: Promotion,
  totalDiscount: number,
  quantityAffectedByLine?: Map<string, number>
): void {
  const base = cartScopeDiscountBase(matching, quantityAffectedByLine);
  if (totalDiscount <= 0 || base <= 0) return;
  const linesById = new Map(lines.map((l) => [l.lineId, l]));
  let allocated = 0;
  const amounts = new Map<string, number>();
  for (let i = 0; i < matching.length; i += 1) {
    const line = matching[i]!;
    const quantity = quantityAffectedByLine?.get(line.lineId);
    const lineBase =
      quantity == null
        ? lineRemainingSubtotal(line)
        : lineRemainingSubtotalForQuantity(line, quantity);
    const share = lineBase / base;
    const slice =
      i === matching.length - 1
        ? roundArsHalfUp(totalDiscount - allocated)
        : roundArsHalfUp(totalDiscount * share);
    allocated = roundArsHalfUp(allocated + slice);
    amounts.set(line.lineId, slice);
  }
  distributeDiscountToLines(linesById, promotion, amounts, quantityAffectedByLine);
}

function applyPercentOff(
  lines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>,
  promotion: Promotion,
  targets: PromotionTarget[]
): void {
  const percent = promotion.valuePercent ?? 0;
  if (percent <= 0) return;

  const matching = eligibleLinesForPromotion(lines, cartLinesById, promotion, targets);
  if (matching.length === 0) return;

  if (promotion.scope === 'cart') {
    const base = matching.reduce((sum, line) => sum + lineRemainingSubtotal(line), 0);
    const totalDiscount = roundArsHalfUp((base * percent) / 100);
    allocateCartScopeDiscount(lines, matching, promotion, totalDiscount);
    return;
  }

  for (const line of matching) {
    const base = lineRemainingSubtotal(line);
    const discount = roundArsHalfUp((base * percent) / 100);
    recordLineDiscount(line, promotion, discount);
  }
}

function applyFixedOff(
  lines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>,
  promotion: Promotion,
  targets: PromotionTarget[]
): void {
  const fixed = promotion.valueFixed ?? 0;
  if (fixed <= 0) return;

  const isProductCombo = promotionRequiresAllProductTargetsInCart(promotion, targets);
  if (isProductCombo) {
    const cartLines = [...cartLinesById.values()];
    if (!cartHasAllProductTargets(cartLines, distinctProductTargetIds(targets))) return;
  }

  const matching = eligibleLinesForPromotion(lines, cartLinesById, promotion, targets);
  if (matching.length === 0) return;

  if (promotion.scope === 'cart') {
    const quantityAffectedByLine = isProductCombo
      ? selectComboParticipantQuantities(matching, targets)
      : undefined;
    if (isProductCombo && quantityAffectedByLine === null) return;
    const participantQuantities = quantityAffectedByLine ?? undefined;
    const discountLines = participantQuantities
      ? matching.filter((line) => participantQuantities.has(line.lineId))
      : matching;
    const base = cartScopeDiscountBase(discountLines, participantQuantities);
    const totalDiscount = roundArsHalfUp(Math.min(fixed, base));
    allocateCartScopeDiscount(
      lines,
      discountLines,
      promotion,
      totalDiscount,
      participantQuantities
    );
    return;
  }

  for (const line of matching) {
    const base = lineRemainingSubtotal(line);
    const discount = roundArsHalfUp(Math.min(fixed, base));
    recordLineDiscount(line, promotion, discount);
  }
}

function applyComboBuyXGetYToLines(
  comboLines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>,
  promotion: Promotion,
  linesById: Map<string, MutableLineState>
): void {
  const buyQty = promotion.comboBuyQty ?? 0;
  const payQty = promotion.comboPayQty ?? 0;
  if (buyQty < 2 || payQty < 1 || payQty >= buyQty) return;

  const freePerSet = buyQty - payQty;
  const slots = expandUnitSlots(comboLines, cartLinesById).sort((a, b) => a.unitPrice - b.unitPrice);
  const perLineDiscount = new Map<string, number>();
  const perLineQty = new Map<string, number>();

  let index = 0;
  while (index + buyQty <= slots.length) {
    const group = slots.slice(index, index + buyQty);
    const freeUnits = group.slice(0, freePerSet);
    for (const unit of freeUnits) {
      perLineDiscount.set(unit.lineId, roundArsHalfUp((perLineDiscount.get(unit.lineId) ?? 0) + unit.unitPrice));
      perLineQty.set(unit.lineId, (perLineQty.get(unit.lineId) ?? 0) + 1);
    }
    index += buyQty;
  }

  distributeDiscountToLines(linesById, promotion, perLineDiscount, perLineQty);
}

function applyComboBuyXGetY(
  lines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>,
  promotion: Promotion,
  targets: PromotionTarget[]
): void {
  const buyQty = promotion.comboBuyQty ?? 0;
  const payQty = promotion.comboPayQty ?? 0;
  if (buyQty < 2 || payQty < 1 || payQty >= buyQty) return;

  const matching = eligibleLinesForPromotion(lines, cartLinesById, promotion, targets);
  if (matching.length === 0) return;

  const linesById = new Map(lines.map((l) => [l.lineId, l]));

  if (promotion.scope === 'line') {
    for (const line of matching) {
      applyComboBuyXGetYToLines([line], cartLinesById, promotion, linesById);
    }
    return;
  }

  applyComboBuyXGetYToLines(matching, cartLinesById, promotion, linesById);
}

function applyComboNForPriceToLines(
  comboLines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>,
  promotion: Promotion,
  linesById: Map<string, MutableLineState>
): void {
  const bundleSize = promotion.comboBuyQty ?? 0;
  const bundlePrice = promotion.comboBundlePrice ?? 0;
  if (bundleSize < 2 || bundlePrice <= 0) return;

  const slots = expandUnitSlots(comboLines, cartLinesById).sort((a, b) => a.unitPrice - b.unitPrice);
  const perLineDiscount = new Map<string, number>();
  const perLineQty = new Map<string, number>();

  let index = 0;
  while (index + bundleSize <= slots.length) {
    const group = slots.slice(index, index + bundleSize);
    const groupSum = group.reduce((sum, u) => sum + u.unitPrice, 0);
    const bundleDiscount = roundArsHalfUp(Math.max(0, groupSum - bundlePrice));
    if (bundleDiscount > 0) {
      let allocated = 0;
      for (let i = 0; i < group.length; i += 1) {
        const unit = group[i]!;
        const share = unit.unitPrice / groupSum;
        const slice =
          i === group.length - 1
            ? roundArsHalfUp(bundleDiscount - allocated)
            : roundArsHalfUp(bundleDiscount * share);
        allocated = roundArsHalfUp(allocated + slice);
        perLineDiscount.set(unit.lineId, roundArsHalfUp((perLineDiscount.get(unit.lineId) ?? 0) + slice));
        perLineQty.set(unit.lineId, (perLineQty.get(unit.lineId) ?? 0) + 1);
      }
    }
    index += bundleSize;
  }

  distributeDiscountToLines(linesById, promotion, perLineDiscount, perLineQty);
}

function applyComboNForPrice(
  lines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>,
  promotion: Promotion,
  targets: PromotionTarget[]
): void {
  const bundleSize = promotion.comboBuyQty ?? 0;
  const bundlePrice = promotion.comboBundlePrice ?? 0;
  if (bundleSize < 2 || bundlePrice <= 0) return;

  const matching = eligibleLinesForPromotion(lines, cartLinesById, promotion, targets);
  if (matching.length === 0) return;

  const linesById = new Map(lines.map((l) => [l.lineId, l]));

  if (promotion.scope === 'line') {
    for (const line of matching) {
      applyComboNForPriceToLines([line], cartLinesById, promotion, linesById);
    }
    return;
  }

  applyComboNForPriceToLines(matching, cartLinesById, promotion, linesById);
}

export function estimatePromotionBenefit(
  promotion: Promotion,
  targets: PromotionTarget[],
  cart: PricedCartLine[],
  lines: MutableLineState[]
): number {
  const isProductCombo = promotionRequiresAllProductTargetsInCart(promotion, targets);
  if (isProductCombo) {
    if (!cartHasAllProductTargets(cart, distinctProductTargetIds(targets))) return 0;
  }

  const cartById = new Map(cart.map((l) => [l.lineId, l]));
  const matching = eligibleLinesForPromotion(lines, cartById, promotion, targets);
  if (matching.length === 0) return 0;

  const base = matching.reduce((sum, line) => sum + lineRemainingSubtotal(line), 0);
  if (base <= 0) return 0;

  const quantityAffectedByLine = isProductCombo
    ? selectComboParticipantQuantities(matching, targets)
    : undefined;
  if (isProductCombo && !quantityAffectedByLine) return 0;
  const benefitBase = quantityAffectedByLine
    ? cartScopeDiscountBase(
        matching.filter((line) => quantityAffectedByLine.has(line.lineId)),
        quantityAffectedByLine
      )
    : base;

  switch (promotion.kind) {
    case 'percent_off': {
      const percent = promotion.valuePercent ?? 0;
      return roundArsHalfUp((base * percent) / 100);
    }
    case 'fixed_off': {
      const fixed = promotion.valueFixed ?? 0;
      if (promotion.scope === 'cart') return roundArsHalfUp(Math.min(fixed, benefitBase));
      // Line scope: each line gets min(fixed, lineSubtotal), not min(fixed, totalBase) * count.
      return roundArsHalfUp(matching.reduce((sum, line) => sum + Math.min(fixed, lineRemainingSubtotal(line)), 0));
    }
    case 'combo_buy_x_get_y': {
      const buyQty = promotion.comboBuyQty ?? 0;
      const payQty = promotion.comboPayQty ?? 0;
      if (buyQty < 2 || payQty < 1 || payQty >= buyQty) return 0;
      const freePerSet = buyQty - payQty;
      const lineGroups =
        promotion.scope === 'line' ? matching.map((line) => [line]) : [matching];
      let benefit = 0;
      for (const groupLines of lineGroups) {
        const slots = expandUnitSlots(groupLines, cartById).sort((a, b) => a.unitPrice - b.unitPrice);
        for (let i = 0; i + buyQty <= slots.length; i += buyQty) {
          const group = slots.slice(i, i + buyQty);
          benefit += group.slice(0, freePerSet).reduce((s, u) => s + u.unitPrice, 0);
        }
      }
      return roundArsHalfUp(benefit);
    }
    case 'combo_n_for_price': {
      const n = promotion.comboBuyQty ?? 0;
      const bundlePrice = promotion.comboBundlePrice ?? 0;
      if (n < 2 || bundlePrice <= 0) return 0;
      const lineGroups =
        promotion.scope === 'line' ? matching.map((line) => [line]) : [matching];
      let benefit = 0;
      for (const groupLines of lineGroups) {
        const slots = expandUnitSlots(groupLines, cartById).sort((a, b) => a.unitPrice - b.unitPrice);
        for (let i = 0; i + n <= slots.length; i += n) {
          const group = slots.slice(i, i + n);
          const sum = group.reduce((s, u) => s + u.unitPrice, 0);
          benefit += Math.max(0, sum - bundlePrice);
        }
      }
      return roundArsHalfUp(benefit);
    }
    default:
      return 0;
  }
}

export function sortPromotionsForEvaluation(
  promotions: PromotionWithTargets[],
  cart: PricedCartLine[],
  lines: MutableLineState[],
  targetsByPromoId: Map<string, PromotionTarget[]>
): PromotionWithTargets[] {
  return [...promotions].sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    const benefitA = estimatePromotionBenefit(a, targetsByPromoId.get(a.id) ?? [], cart, lines);
    const benefitB = estimatePromotionBenefit(b, targetsByPromoId.get(b.id) ?? [], cart, lines);
    if (benefitB !== benefitA) return benefitB - benefitA;
    const updatedA = Date.parse(a.updatedAt) || 0;
    const updatedB = Date.parse(b.updatedAt) || 0;
    return updatedB - updatedA;
  });
}

export function checkPromotionConditions(
  promotion: Promotion,
  cart: PricedCartLine[],
  now: Date,
  usageCount?: number,
  timezoneOffsetMinutes?: number
): boolean {
  const conditions: PromotionConditions | null | undefined = promotion.conditions;
  if (!conditions) return true;

  const cartSubtotal = cart.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
  const cartQty = cart.reduce((sum, l) => sum + l.quantity, 0);

  if (conditions.minPurchaseAmount != null && cartSubtotal < conditions.minPurchaseAmount) {
    return false;
  }

  if (conditions.minQuantity != null && cartQty < conditions.minQuantity) {
    return false;
  }

  if (conditions.usageLimit != null) {
    const current = usageCount ?? 0;
    if (current >= conditions.usageLimit) return false;
  }

  // Compute local-time minutes/day from the runtime `now`, adjusted by an
  // optional timezone offset. Without this, edge functions running in UTC
  // would evaluate scheduleWindow/weekdays in UTC instead of the business
  // local time (e.g. Argentina UTC-3).
  const offsetMs = (timezoneOffsetMinutes ?? 0) * 60 * 1000;
  const local = new Date(now.getTime() + offsetMs);
  const localMinutes = local.getUTCHours() * 60 + local.getUTCMinutes();
  const localDay = local.getUTCDay() === 0 ? 7 : local.getUTCDay();

  if (conditions.weekdays && conditions.weekdays.length > 0) {
    if (!conditions.weekdays.includes(localDay as 1 | 2 | 3 | 4 | 5 | 6 | 7)) return false;
  }

  if (conditions.scheduleWindow) {
    const { startTime, endTime } = conditions.scheduleWindow;
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    if (Number.isFinite(sh) && Number.isFinite(sm) && Number.isFinite(eh) && Number.isFinite(em)) {
      const startMin = sh * 60 + sm;
      const endMin = eh * 60 + em;
      // Cross-midnight window (e.g. 22:00–02:00): active if localMinutes >= startMin
      // OR localMinutes <= endMin. Normal window: active if startMin <= localMinutes <= endMin.
      if (startMin <= endMin) {
        if (localMinutes < startMin || localMinutes > endMin) return false;
      } else {
        if (localMinutes < startMin && localMinutes > endMin) return false;
      }
    }
  }

  return true;
}

function filterActivePromotionsForEvaluation(
  promotions: PromotionWithTargets[],
  cart: PricedCartLine[],
  now: Date,
  branchId: string | null | undefined,
  options?: CartEvaluationOptions
): PromotionWithTargets[] {
  const normalizedCode = normalizePromoCode(options?.promoCode);
  const selectedIds = new Set(options?.selectedPromotionIds ?? []);
  const usageCounts = options?.usageCountByPromotionId;
  const manualCodes = new Set<string>();
  if (normalizedCode) manualCodes.add(normalizedCode);
  for (const raw of options?.promoCodes ?? []) {
    const code = normalizePromoCode(raw);
    if (code) manualCodes.add(code);
  }

  return promotions.filter((promo) => {
    if (!promo.isActive || promo.deletedAt) return false;
    if (!AUTO_KINDS.includes(promo.kind)) return false;
    if (!isPromotionScheduleActive(promo, now)) return false;
    if (!promotionAppliesToBranch(promo, branchId)) return false;
    if (!checkPromotionConditions(promo, cart, now, usageCountForPromotion(usageCounts, promo.id), options?.timezoneOffsetMinutes)) return false;

    if (promo.applyMode === 'automatic') return true;
    if (promo.applyMode === 'manual_code') {
      if (manualCodes.size === 0 || !promo.promoCode) return false;
      return manualCodes.has(promo.promoCode.trim().toUpperCase());
    }
    if (promo.applyMode === 'cashier_select') {
      return selectedIds.has(promo.id);
    }
    return false;
  });
}

function applyPromotion(
  promotion: Promotion,
  targets: PromotionTarget[],
  lines: MutableLineState[],
  cartLinesById: Map<string, PricedCartLine>
): void {
  switch (promotion.kind) {
    case 'percent_off':
      applyPercentOff(lines, cartLinesById, promotion, targets);
      break;
    case 'fixed_off':
      applyFixedOff(lines, cartLinesById, promotion, targets);
      break;
    case 'combo_buy_x_get_y':
      applyComboBuyXGetY(lines, cartLinesById, promotion, targets);
      break;
    case 'combo_n_for_price':
      applyComboNForPrice(lines, cartLinesById, promotion, targets);
      break;
    default:
      break;
  }
}

function toEvaluatedLines(lines: MutableLineState[]): EvaluatedCartLine[] {
  return lines.map((line) => {
    const gross = roundArsHalfUp(line.unitPriceOriginal * line.quantity);
    const lineDiscountTotal = roundArsHalfUp(line.lineDiscountTotal);
    const lineSubtotal = roundArsHalfUp(Math.max(0, gross - lineDiscountTotal));
    const unitPrice =
      line.quantity > 0 ? roundArsHalfUp(lineSubtotal / line.quantity) : line.unitPriceOriginal;
    return {
      lineId: line.lineId,
      productId: line.productId,
      quantity: line.quantity,
      unitPriceOriginal: line.unitPriceOriginal,
      unitPrice,
      lineSubtotal,
      lineDiscountTotal,
      appliedDiscounts: line.appliedDiscounts,
    };
  });
}

/**
 * Evalúa promociones sobre un carrito (automáticas, código o selección cajero).
 * @param cart Líneas con precio unitario base.
 * @param promotions Promociones con targets embebidos o vacíos (= todos los productos elegibles).
 * @param now Instante de evaluación (vigencia starts_at/ends_at).
 * @param branchId Sucursal activa (filtro branch_ids).
 * @param options Código promo normalizado y/o IDs de promos elegidas por cajero.
 */
export function evaluateCart(
  cart: PricedCartLine[],
  promotions: PromotionWithTargets[],
  now: Date | string = new Date(),
  branchId?: string | null,
  options?: CartEvaluationOptions
): CartEvaluationResult {
  return runWithMoneyScale(resolvePromotionMoneyScale(options?.currency), () =>
    evaluateCartWithActiveScale(cart, promotions, now, branchId, options),
  );
}

function evaluateCartWithActiveScale(
  cart: PricedCartLine[],
  promotions: PromotionWithTargets[],
  now: Date | string = new Date(),
  branchId?: string | null,
  options?: CartEvaluationOptions
): CartEvaluationResult {
  const evaluatedAt = typeof now === 'string' ? new Date(now) : now;
  const cartLinesById = new Map(cart.map((line) => [line.lineId, line]));

  const lines: MutableLineState[] = cart.map((line) => ({
    lineId: line.lineId,
    productId: line.productId,
    categoryId: line.categoryId,
    brandId: line.brandId,
    unitPriceOriginal: line.unitPrice,
    quantity: line.quantity,
    lineDiscountTotal: 0,
    appliedDiscounts: [],
    hasNonStackablePromo: false,
  }));

  const targetsByPromoId = buildTargetsByPromotionId(promotions);
  const active = filterActivePromotionsForEvaluation(promotions, cart, evaluatedAt, branchId, options);
  const ordered = sortPromotionsForEvaluation(active, cart, lines, targetsByPromoId);

  for (const promotion of ordered) {
    const targets = targetsByPromoId.get(promotion.id) ?? [];
    applyPromotion(promotion, targets, lines, cartLinesById);
  }

  const evaluatedLines = toEvaluatedLines(lines);
  const subtotal = roundArsHalfUp(
    evaluatedLines.reduce((sum, line) => sum + line.unitPriceOriginal * line.quantity, 0)
  );
  const discountTotal = roundArsHalfUp(
    evaluatedLines.reduce((sum, line) => sum + line.lineDiscountTotal, 0)
  );
  const total = roundArsHalfUp(Math.max(0, subtotal - discountTotal));

  const cartDiscounts: AppliedDiscount[] = [];
  for (const line of evaluatedLines) {
    for (const discount of line.appliedDiscounts) {
      if (discount.scope === 'cart') {
        cartDiscounts.push(discount);
      }
    }
  }

  return {
    lines: evaluatedLines,
    subtotal,
    discountTotal,
    total,
    cartDiscounts,
  };
}
